import { createStore } from "vuex";
import {
  getDatabase,
  ref as dbRef,
  query,
  get,
  update,
  push,
  equalTo,
  orderByChild,
} from "firebase/database";

const parseTimestamp = (ts) => {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  const parsed = Date.parse(ts);
  if (!Number.isNaN(parsed)) return parsed;
  const d = new Date(ts).getTime();
  return Number.isNaN(d) ? 0 : d;
};

const store = createStore({
  state() {
    return {
      books: {}, // Store multiple books as an object
    };
  },
  mutations: {
    SET_BOOKS(state, books) {
      state.books = books;
    },
    ADD_BOOK(state, book) {
      state.books = { ...state.books, [book.id]: book };
    },
    UPDATE_BOOK(state, { id, book }) {
      state.books = {
        ...state.books,
        [id]: { ...(state.books[id] || {}), ...book },
      };
    },
    DELETE_BOOK(state, id) {
      const updated = { ...state.books };
      delete updated[id];
      state.books = updated;
    },
  },
  actions: {
    async fetchBooks({ commit }, uid) {
      if (!uid) {
        commit("SET_BOOKS", {});
        return;
      }
      const db = getDatabase();
      const booksRef = dbRef(db, "book");

      try {
        const booksQuery = query(
          booksRef,
          orderByChild("user_id"),
          equalTo(uid)
        );

        const snapshot = await get(booksQuery);
        const data = snapshot.val();
        if (data) {
          // Convert the object into an array, sort by timestamp, and then commit to the store
          const sortedBooks = Object.entries(data)
            .map(([key, book]) => ({ key, ...book })) // Map the entries to include the key as part of the book object
            .sort((a, b) => {
              const dateA = parseTimestamp(a.timestamp);
              const dateB = parseTimestamp(b.timestamp);
              return dateB - dateA; // Sort in descending order (newest first)
            })
            .reduce((acc, { key, ...book }) => {
              acc[key] = book; // Use the original keys while keeping the sorted books
              return acc;
            }, {});

          commit("SET_BOOKS", sortedBooks);
        } else {
          commit("SET_BOOKS", {});
          console.warn("No books found.");
        }
      } catch (error) {
        console.error("Error fetching books:", error);
      }
    },
    async fetchBookById({ commit }, id) {
      if (!id) return null;
      const db = getDatabase();
      const bookRef = dbRef(db, `book/${id}`);
      try {
        const snapshot = await get(bookRef);
        const data = snapshot.val();
        if (data) {
          const bookObj = { id, ...data };
          commit("ADD_BOOK", bookObj);
          return bookObj;
        }
      } catch (error) {
        console.error(`Error fetching book ${id}:`, error);
      }
      return null;
    },
    async addBook({ commit }, book) {
      const db = getDatabase();
      const booksRef = dbRef(db, "book");
      try {
        const newBookRef = await push(booksRef, book);
        commit("ADD_BOOK", { id: newBookRef.key, ...book });
      } catch (error) {
        console.error("Error adding book:", error);
      }
    },
    async updateBook({ commit }, { id, book }) {
      const db = getDatabase();
      const bookRef = dbRef(db, `book/${id}`);
      try {
        await update(bookRef, book);
        commit("UPDATE_BOOK", { id, book });
      } catch (error) {
        console.error("Error updating book:", error);
      }
    },
    async deleteBook({ commit }, id) {
      const db = getDatabase();
      const bookRef = dbRef(db, `book/${id}`);
      try {
        await update(bookRef, { user_id: "" });
        commit("DELETE_BOOK", id);
      } catch (error) {
        console.error("Error deleting book:", error);
      }
    },
  },
  getters: {
    allBooks(state) {
      return state.books;
    },
    getBook: (state) => (id) => {
      return state.books[id];
    },
  },
});

export default store;
